import React, { Dispatch, SetStateAction, useState, useMemo, useEffect } from 'react';
import { ILxsCreateOrderResources } from '../lxs-create-order.props.autogenerated';
import {
    ColumnDef,
    ColumnFiltersState,
    SortingState,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable
} from '@tanstack/react-table';
import { IGetAllProductsResult } from '../../../actions/get-all-products.action';
import SearchBox from './search-box';
import { ILxsOrderData } from '../lxs-create-order.data';
import { IPostCartItems } from '../../../actions/post-cart.action';
import Alert from '../../../components/alert';
import highlightSearchTerm from './highlight-search-term';
import { toast } from 'sonner';
import Skeleton from '../../../components/skeleton';
import AddressInformation from '../components/address-information';
import { IShipToTableData } from '../../../actions/get-ship-tos.action';
import SelectedProducts from './selected-products';
import {
    NO_DATA_FALLBACK,
    CREATE_ORDER_NO_ENDCUSTOMER_VALUE,
    CREATE_ORDER_COLUMN_PRODUCT_NAME,
    CREATE_ORDER_COLUMN_PRODUCT_NUMBER,
    CREATE_ORDER_COLUMN_ACTION,
    CREATE_ORDER_COLUMN_UNIT,
    CREATE_ORDER_COLUMN_INCOTERM,
    CREATE_ORDER_COLUMN_SALES_ORG,
    CREATE_ORDER_COLUMN_SALES_ORG_TEXT,
    CREATE_ORDER_COLUMN_BUSINESS_UNIT,
    CREATE_ORDER_COLUMN_DIVISION_TEXT,
    CREATE_ORDER_COLUMN_DISTRIBUTION_CHANNEL
} from '../../../configuration';
import { useModal } from '../../../components/modal';

export default function products({
    step,
    resources,
    orderData,
    pageSize,
    globalFilter,
    productsData,
    columnFilters,
    isColumnFilterValid,
    filterDisabled,
    productsHaveEndcustomers,
    globalDate,
    showAddressInformation,
    fetchPrices,
    setShipTo,
    setStep,
    setIsColumnFilterValid,
    setColumnFilters,
    setProducts,
    setGlobalFilter,
    setFilterDisabled,
    pushHistoryState,
    setGlobalDate
}: {
    step: number;
    resources: ILxsCreateOrderResources;
    orderData: ILxsOrderData;
    pageSize: number;
    globalFilter: string;
    productsData: IGetAllProductsResult[] | undefined;
    columnFilters: ColumnFiltersState;
    isColumnFilterValid: boolean;
    filterDisabled: boolean;
    productsHaveEndcustomers: boolean;
    globalDate: Date;
    showAddressInformation: boolean;
    fetchPrices: boolean;
    setShipTo: (shipTo: IShipToTableData) => void;
    setStep: Dispatch<SetStateAction<number>>;
    setIsColumnFilterValid: React.Dispatch<React.SetStateAction<boolean>>;
    setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
    setProducts: Dispatch<SetStateAction<IPostCartItems[]>>;
    setGlobalFilter: Dispatch<SetStateAction<string>>;
    setFilterDisabled: Dispatch<SetStateAction<boolean>>;
    pushHistoryState: (currentStep: number) => void;
    setGlobalDate: React.Dispatch<React.SetStateAction<Date>>;
}) {
    const [pageIndex, setPageIndex] = useState<number>(0);

    const { setOpen, setClose } = useModal();

    const columns = useMemo<ColumnDef<IGetAllProductsResult, any>[]>(
        () => [
            {
                accessorFn: row => row.ProductName,
                id: CREATE_ORDER_COLUMN_PRODUCT_NAME,
                cell: info => highlightSearchTerm(String(info.getValue()), globalFilter) ?? NO_DATA_FALLBACK,
                header: () => resources.createOrderProductsTableProductName,
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.ProductNumber,
                id: CREATE_ORDER_COLUMN_PRODUCT_NUMBER,
                cell: info => highlightSearchTerm(String(info.getValue()), globalFilter) ?? NO_DATA_FALLBACK,
                header: () => resources.createOrderProductsTableProductNumber,
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.UnitOfMeasure,
                id: CREATE_ORDER_COLUMN_UNIT,
                cell: info => highlightSearchTerm(String(info.getValue()), globalFilter) ?? NO_DATA_FALLBACK,
                header: () => resources.createOrderProductsTableUnit,
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.Incoterm1,
                id: CREATE_ORDER_COLUMN_INCOTERM,
                cell: info => highlightSearchTerm(String(info.getValue()), globalFilter) ?? NO_DATA_FALLBACK,
                header: () => resources.createOrderProductsTableIncoterm,
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.SalesOrg,
                id: CREATE_ORDER_COLUMN_SALES_ORG,
                cell: info => info.getValue()
            },
            {
                accessorFn: row => row.Division,
                id: CREATE_ORDER_COLUMN_BUSINESS_UNIT,
                cell: info => info.getValue()
            },
            {
                id: CREATE_ORDER_COLUMN_ACTION,
                cell: info => info.getValue(),
                header: () => resources.createOrderProductsTableActionTitle,
                footer: props => props.column.id,
                enableSorting: false
            }
        ],
        [globalFilter]
    );

    /* CAUTION: data needs to be defined for table,
      productsData can be undefined in order to show Skeleton while loading.
      They are similar but not the same. */
    const data = productsData ?? [];
    const [columnVisibility] = useState<VisibilityState>({
        [CREATE_ORDER_COLUMN_SALES_ORG]: false,
        [CREATE_ORDER_COLUMN_BUSINESS_UNIT]: false
    });
    const [sorting, setSorting] = useState<SortingState>([]);

    const table = useReactTable({
        data,
        columns,
        filterFns: {},
        state: {
            sorting,
            columnVisibility,
            columnFilters,
            globalFilter,
            pagination: {
                pageIndex: pageIndex,
                pageSize: pageSize
            }
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: 'includesString',
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        debugTable: false,
        debugHeaders: false,
        debugColumns: false,
        autoResetAll: false
    });
    const totalEntries: number = table.getFilteredRowModel().rows.length;

    const getSalesOrgOptions = () => {
        const salesOrgOptions: { value: string; label: string }[] = data
            .map(data => ({ value: data.SalesOrg, label: data.SalesOrgText }))
            .filter(item => !!item?.value && !!item?.label);
        return [...new Map(salesOrgOptions.map(v => [v?.value, v])).values()];
    };
    const getBusinessUnitOptions = () => {
        const businessUnitOptions = data
            .map(data => ({ value: data.Division, label: data.DivisionText }))
            .filter(item => !!item?.value && !!item?.label);
        return [...new Map(businessUnitOptions.map(v => [v?.value, v])).values()];
    };
    const getIncotermOptions = () => {
        const incotermOptions = data
            .map(data => ({ value: data.Incoterm1, label: data.Incoterm1 }))
            .filter(item => !!item?.value && !!item?.label);
        return [...new Map(incotermOptions.map(v => [v?.value, v])).values()];
    };

    useEffect(() => {
        if (data.length > 0 && totalEntries === 0 && columnFilters.length > 0) {
            setIsColumnFilterValid(false);
        }
    }, [data, columnFilters]);

    useEffect(() => {
        setPageIndex(0);
    }, [globalFilter, columnFilters]);

    return (
        <div>
            <div className='row'>
                <div
                    className={`col ${showAddressInformation ? 'lxs-create-order__search--small' : 'col lxs-create-order__search--large'}`}
                >
                    <SearchBox
                        filterDisabled={filterDisabled}
                        columnFilters={columnFilters}
                        globalFilter={globalFilter}
                        resources={resources}
                        isColumnFilterValid={isColumnFilterValid}
                        setGlobalFilter={setGlobalFilter}
                        setColumnFilters={setColumnFilters}
                        incotermOptions={getIncotermOptions()}
                        salesOrgOptions={getSalesOrgOptions()}
                        businessUnitOptions={getBusinessUnitOptions()}
                    />

                    {totalEntries > 0 && (
                        <h3 className='lxs-create-order__total-entries'>
                            {totalEntries} {resources.createOrderSearchProductsHeading}
                        </h3>
                    )}

                    <div className='table-responsive'>
                        <table className='table'>
                            <thead>
                                {table.getHeaderGroups().map(headerGroup => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map(header => {
                                            const sortValue =
                                                { asc: 'msc-table-sorting--asc', desc: 'msc-table-sorting--desc' }[
                                                    header.column.getIsSorted() as string
                                                ] ?? null;
                                            return (
                                                <th key={header.id} colSpan={header.colSpan}>
                                                    {header.isPlaceholder ? null : (
                                                        <div
                                                            className={
                                                                header.column.getCanSort()
                                                                    ? `msc-table-sorting ${sortValue ?? 'msc-table-sorting--default'} `
                                                                    : ''
                                                            }
                                                            onClick={header.column.getToggleSortingHandler()}
                                                        >
                                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                                        </div>
                                                    )}
                                                </th>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {!productsData ? (
                                    Array.from({ length: pageSize }).map(() => {
                                        return table.getHeaderGroups().map(headerGroup => (
                                            <tr key={headerGroup.id}>
                                                {headerGroup.headers.map(column => (
                                                    <td key={column.id}>
                                                        <Skeleton />
                                                    </td>
                                                ))}
                                            </tr>
                                        ));
                                    })
                                ) : productsData.length === 0 ? (
                                    <td colSpan={9} className='no-padding'>
                                        <Alert message={resources.createOrderNoDataMessage} type='info' iconSize={1} />
                                    </td>
                                ) : table.getRowModel().rows?.length ? (
                                    table.getRowModel().rows.map(row => (
                                        <React.Fragment key={row.id}>
                                            <tr>
                                                {row.getVisibleCells().map(cell => {
                                                    return cell.column?.id === CREATE_ORDER_COLUMN_ACTION ? (
                                                        <td>
                                                            {/* ADD NEW PRODUCT TO SELECTED PRODUCTS */}
                                                            <a
                                                                className='lxs-create-order__add-to-cart-button'
                                                                onClick={() => {
                                                                    const products = orderData.products ? orderData.products.slice() : [];
                                                                    products.push({
                                                                        CustomerAccountNumber: '',
                                                                        Number: 0,
                                                                        Quantity: '',
                                                                        ProductNumber: row.original.ProductNumber,
                                                                        ProductName: row.original.ProductName,
                                                                        CustomerProductNumber: row.original.CustomerProductNumber,
                                                                        RequestedDate: globalDate,
                                                                        UnitOfMeasure: row.original.BaseUnit,
                                                                        EndCustomerNumber: productsHaveEndcustomers
                                                                            ? ''
                                                                            : CREATE_ORDER_NO_ENDCUSTOMER_VALUE,
                                                                        EndCustomerName: productsHaveEndcustomers
                                                                            ? ''
                                                                            : CREATE_ORDER_NO_ENDCUSTOMER_VALUE,
                                                                        PackagingQty: row.original.PackagingQty,
                                                                        MinimumQty: '',
                                                                        MinimumQtyUnit: '',
                                                                        MinimumQtyOnOrderLevel: false
                                                                    });

                                                                    if (!filterDisabled) {
                                                                        setFilterDisabled(true);
                                                                        const productColumnFilters = [
                                                                            {
                                                                                id: CREATE_ORDER_COLUMN_BUSINESS_UNIT,
                                                                                value: row.original.Division
                                                                            },
                                                                            {
                                                                                id: CREATE_ORDER_COLUMN_DIVISION_TEXT,
                                                                                value: row.original.DivisionText
                                                                            },
                                                                            {
                                                                                id: CREATE_ORDER_COLUMN_SALES_ORG,
                                                                                value: row.original.SalesOrg
                                                                            },
                                                                            {
                                                                                id: CREATE_ORDER_COLUMN_SALES_ORG_TEXT,
                                                                                value: row.original.SalesOrgText
                                                                            },
                                                                            {
                                                                                id: CREATE_ORDER_COLUMN_INCOTERM,
                                                                                value: row.original.Incoterm1
                                                                            },
                                                                            {
                                                                                id: CREATE_ORDER_COLUMN_DISTRIBUTION_CHANNEL,
                                                                                value: row.original.DistributionChannel
                                                                            }
                                                                        ];
                                                                        setColumnFilters(productColumnFilters);
                                                                    }

                                                                    // Give user feedback that the item was added to the cart
                                                                    toast.success(
                                                                        <div className='toast' role='alert'>
                                                                            <div className='toast-header'>
                                                                                <h4>{resources.createOrderItemAddedHeading}</h4>
                                                                                <button
                                                                                    className='toast-close'
                                                                                    onClick={() => toast.dismiss()}
                                                                                ></button>
                                                                            </div>
                                                                            <div className='toast-body'>
                                                                                <span>{resources.createOrderItemAddedDescription}</span>
                                                                            </div>
                                                                        </div>
                                                                    );

                                                                    setProducts(products);
                                                                    // Scroll to the selected products container to show the added product
                                                                    const selectedProductsContainer = document.querySelector(
                                                                        '.msc-pagination'
                                                                    );
                                                                    selectedProductsContainer?.scrollIntoView({
                                                                        behavior: 'smooth'
                                                                    });
                                                                }}
                                                            >
                                                                {resources.createOrderProductsTableActionLabel}
                                                            </a>
                                                        </td>
                                                    ) : (
                                                        <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                                                    );
                                                })}
                                            </tr>
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={9} className='no-padding'>
                                            <Alert message={resources.createOrderEmptyTableMessage} type='info' iconSize={1} />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalEntries > 0 && totalEntries > pageSize && (
                        <div className='msc-pagination'>
                            <button
                                className='msc-pagination__button msc-pagination__button--is-previous'
                                onClick={() => setPageIndex(pageIndex - 1)}
                                disabled={!table.getCanPreviousPage()}
                            ></button>
                            <span className='msc-pagination__page-count'>
                                {table.getState().pagination.pageIndex + 1} / {table.getPageCount()}
                            </span>
                            <button
                                className='msc-pagination__button msc-pagination__button--is-next'
                                onClick={() => setPageIndex(pageIndex + 1)}
                                disabled={!table.getCanNextPage()}
                            ></button>
                        </div>
                    )}
                </div>
                <div
                    className={`col-12 pl-lg-0 lxs-create-order__address-information-wrapper ${
                        showAddressInformation
                            ? 'lxs-create-order__address-information-wrapper--show'
                            : 'lxs-create-order__address-information-wrapper--hide'
                    }`}
                >
                    <AddressInformation
                        soldTo={orderData.soldTo}
                        shipTo={orderData.shipTo ?? ({} as IShipToTableData)}
                        resources={resources}
                    />
                </div>
            </div>
            {/* SelectedProducts table */}
            {orderData.products && orderData.products.length > 0 && (
                <SelectedProducts
                    goto={step => setStep(step)}
                    orderData={orderData}
                    productsData={productsData}
                    resources={resources}
                    productsHaveEndcustomers={productsHaveEndcustomers}
                    setProducts={setProducts}
                    globalDate={globalDate}
                    setGlobalDate={setGlobalDate}
                    headerColumnFilters={columnFilters}
                    fetchPrices={fetchPrices}
                />
            )}

            {/* Navigation */}
            <div className='msc-form__button-group lxs-create-order__direction-button-wrapper'>
                <button
                    className='msc-cta__secondary'
                    type='button'
                    onClick={() => {
                        if (orderData?.products !== undefined && orderData.products.length > 0) {
                            setOpen({
                                content: (
                                    <div>
                                        <p className='mb-2'>{resources.createOrderDeleteProductsHint}</p>
                                        <a
                                            className='msc-cta__primary'
                                            onClick={() => {
                                                setShipTo({} as IShipToTableData);
                                                pushHistoryState(step - 1);
                                                setStep?.(step - 1);
                                                setClose();
                                            }}
                                        >
                                            {resources.createOrderDeleteProductsHintConfirm}
                                        </a>
                                    </div>
                                ),
                                size: 'sm'
                            });
                        } else {
                            setShipTo({} as IShipToTableData);
                            pushHistoryState(step - 1);
                            setStep?.(step - 1);
                        }
                    }}
                >
                    {resources.createOrderPreviousPageButton}
                </button>
                <button
                    disabled={!orderData?.products || orderData?.products?.length === 0}
                    className='msc-cta__primary'
                    type='submit'
                    form='selected-products'
                >
                    {resources.createOrderNextPageButton}
                </button>
            </div>
        </div>
    );
}
